import React, {
  ChangeEventHandler,
  HTMLInputTypeAttribute,
  ReactNode,
} from 'react'
import { FieldHookConfig, useField, useFormikContext } from 'formik'
import { SizeVariants } from '@/shared/forms/components/constants'
import clsx from 'clsx'
import OptionalLabel from '@/shared/forms/components/OptionalLabel'
import { SimpleTooltip } from '@/shared/components/common/Tooltip'

export type Option = {
  id: string
  label: string
}

export type FormikInputProps = {
  label: string
  size: keyof typeof SizeVariants
  autocomplete: string
  options: Option[]
  disabled?: boolean
  placeholder?: string
  type?: HTMLInputTypeAttribute
  optional?: boolean
  tooltip?: ReactNode
  inputClassName?: string
  appearance?: 'default' | 'portal-profile'

  emptyLabel?: string
  onChange?: ChangeEventHandler<HTMLSelectElement>
} & FieldHookConfig<string>

export default function FormikDropdown({
  label,
  size,
  options,
  autocomplete,
  disabled,
  placeholder,
  emptyLabel,
  onChange,
  tooltip,
  inputClassName,
  appearance = 'default',
  optional = false,
  ...props
}: FormikInputProps) {
  const [field, meta] = useField(props)
  const { isSubmitting } = useFormikContext()
  const isDisabled = disabled || isSubmitting
  const typedOptions = options as Option[]
  const inputId = props.id ?? field.name

  return (
    <div className={SizeVariants[size as keyof typeof SizeVariants]}>
      {label && (
        <div className="flex items-end justify-between">
          <div className="flex items-center gap-x-2">
            <label
              htmlFor={inputId}
              className={clsx(
                'block text-sm font-medium leading-6',
                disabled ? 'text-gray-500' : 'text-gray-900',
              )}
            >
              {label}
            </label>
            {tooltip && <SimpleTooltip content={tooltip} />}
          </div>
          <OptionalLabel optional={optional} />
        </div>
      )}
      <select
        id={inputId}
        name={field.name}
        className={clsx(
          appearance === 'portal-profile'
            ? 'mt-2 block h-[52px] w-full rounded-[14px] border-[1.5px] bg-[#F4F4F6] px-4 py-0 text-[15px] leading-5 text-[#0B0B0F] outline-none transition-colors duration-300 focus:border-[#0B0B0F]/45'
            : 'mt-2 block h-10 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6',
          meta.touched && meta.error
            ? appearance === 'portal-profile'
              ? 'border-[#C6453C]'
              : 'ring-red-600'
            : appearance === 'portal-profile'
              ? 'border-transparent'
              : 'ring-gray-300',
          isDisabled
            ? appearance === 'portal-profile'
              ? 'cursor-not-allowed opacity-60'
              : 'bg-slate-300'
            : '',
          inputClassName,
        )}
        value={field.value}
        onChange={onChange ? onChange : field.onChange}
        onBlur={field.onBlur}
        disabled={isDisabled}
      >
        {emptyLabel && (
          <option className="hidden" value="" label={emptyLabel} />
        )}
        {typedOptions.map(({ id, label }) => (
          <option key={`${props.id}-${id}`} value={id}>
            {label}
          </option>
        ))}
      </select>
      {meta.touched && meta.error && (
        <div className="mt-2 text-sm font-semibold text-red-600">
          {meta.error}
        </div>
      )}
    </div>
  )
}
